import { onScopeDispose, ref, shallowRef } from 'vue'
import type { PlacesProvider, PlaceSuggestion, ResolvedPlace } from '@gpx-spa/geo-domain'

const DEFAULT_DEBOUNCE_MS = 300

export interface UsePlaceAutocompleteOptions {
  /** Delay before calling the provider after the user stops typing (default 300). */
  debounceMs?: number
}

export function usePlaceAutocomplete(
  places: PlacesProvider,
  options: UsePlaceAutocompleteOptions = {}
) {
  const debounceMs = options.debounceMs ?? DEFAULT_DEBOUNCE_MS

  const query = ref('')
  const suggestions = shallowRef<PlaceSuggestion[]>([])
  const loading = ref(false)
  const error = ref<Error | null>(null)
  const resolved = shallowRef<ResolvedPlace | null>(null)
  /** Query string of the last successful `searchPlaces` (trimmed). Used to reopen suggestions without a new API call. */
  const lastFetchedQuery = ref('')
  let abortController: AbortController | null = null
  let debounceTimer: ReturnType<typeof setTimeout> | null = null

  function clearDebounce() {
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
  }

  function refreshSuggestionsForOverlay() {
    if (suggestions.value.length > 0) {
      suggestions.value = [...suggestions.value]
    }
  }

  async function runSearch(value: string) {
    const trimmed = value.trim()
    if (trimmed === lastFetchedQuery.value && suggestions.value.length > 0) {
      refreshSuggestionsForOverlay()
      return
    }

    abortController?.abort()
    abortController = new AbortController()
    const signal = abortController.signal

    if (!trimmed) {
      suggestions.value = []
      lastFetchedQuery.value = ''
      loading.value = false
      return
    }

    loading.value = true
    error.value = null
    try {
      suggestions.value = await places.searchPlaces(value, { signal })
      lastFetchedQuery.value = trimmed
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        return
      }
      error.value = e instanceof Error ? e : new Error(String(e))
      suggestions.value = []
    } finally {
      loading.value = false
    }
  }

  function onInput(value: string) {
    query.value = value
    resolved.value = null
    clearDebounce()
    abortController?.abort()

    if (!value.trim()) {
      suggestions.value = []
      lastFetchedQuery.value = ''
      loading.value = false
      error.value = null
      return
    }

    const trimmed = value.trim()
    if (trimmed === lastFetchedQuery.value && suggestions.value.length > 0) {
      refreshSuggestionsForOverlay()
      return
    }

    debounceTimer = setTimeout(() => {
      debounceTimer = null
      void runSearch(value)
    }, debounceMs)
  }

  async function select(suggestion: PlaceSuggestion) {
    clearDebounce()
    loading.value = true
    error.value = null
    try {
      resolved.value = await places.resolvePlace(suggestion)
      query.value = resolved.value.formattedAddress
      suggestions.value = []
      lastFetchedQuery.value = ''
    } catch (e) {
      error.value = e instanceof Error ? e : new Error(String(e))
    } finally {
      loading.value = false
    }
  }

  function clear() {
    clearDebounce()
    abortController?.abort()
    query.value = ''
    suggestions.value = []
    lastFetchedQuery.value = ''
    resolved.value = null
    error.value = null
    loading.value = false
  }

  onScopeDispose(() => {
    clearDebounce()
    abortController?.abort()
  })

  return { query, suggestions, loading, error, resolved, onInput, select, clear }
}
